import { useMemo, useState } from 'react';
import { REGROUPEMENTS, type Regroupement } from '@nextilt/commun';
import type { Bulle, BulleEnrichie, Feuille } from '../partage/api/types';
import { IconeCombine } from '../partage/icones';
import { Curseur, type Cran } from '../partage/PiecesDuCarnet';
import { dureeLisible, jourAbrege } from '../partage/datesEnMots';
import { useGroupes } from '../partage/useArbre';
import {
  AUCUNE_DUREE_REGLEE,
  AVANCEMENT,
  compteARebours,
  ECHEANCE,
  ECHEANCES_QUI_APPROCHENT,
  FAITES,
  faitesSurTotal,
  MOTS_DES_REGROUPEMENTS,
  nAutres,
  OU_PASSE_LE_TEMPS,
  REGROUPER_PAR,
  RENDEZ_VOUS,
  RESTANTES,
  RIEN_DE_DATE_A_VENIR,
  SANS_DUREE,
} from '../partage/vocabulaire';
import { titreDuRond } from './rondQuiCoche';
import {
  brancheDeLaCarte,
  cartesDuGroupe,
  choisirLeRegroupementDuCouche,
  ouvertesDuCouche,
  perlesDeLAvancement,
  regroupementDuCouche,
} from './lignesDeLAnalyseCouchee';
import { attendLeMonde, ceQueDitLeBout } from './arbreAuPouce';
import { groupesALEcran } from './groupesALEcran';
import { barreDeLAvancement, enPeu, ouPasseLeTemps } from './lignesDuPanneauDAnalyse';
import './AnalyseCouchee.css';

/** Les quatre crans de « Regrouper par », dans l'ordre du serveur et de la maquette. */
const CRANS_DU_REGROUPEMENT: readonly Cran<Regroupement>[] = REGROUPEMENTS.map((r) => [r, MOTS_DES_REGROUPEMENTS[r]] as const);

/**
 * **L'Analyse du téléphone couché** (chantier telephone-couche, 02 ; maquette
 * `app-au-telephone.html`, `analyse()`, `.analyse`, `.an-g`, `.an-d`). À gauche, sur 262 px, les
 * blocs : l'avancement en perles, « N/M faites » et le temps qui reste (sans la route : l'app ne
 * connaît pas le trajet) ; les échéances qui approchent en J-n, la proche en corail ; où passe le
 * temps, une barre par étape. À droite, « Regrouper par » à quatre crans et les tâches en cartes,
 * dans les colonnes des groupes du serveur, tout ouvert : seules les bulles au bout sont des cartes.
 *
 * Rien n'est compté ici : `Feuille.analyse` et `GET /api/feuille/:id/groupes`, rangés par les
 * règles du panneau de l'ordinateur (`lignesDuPanneauDAnalyse.ts`, `groupesALEcran.ts`) et
 * `analyseCouchee.ts`. Une carte touchée ouvre sa fiche, sa case la coche.
 */
export function AnalyseCouchee({
  feuille,
  onFiche,
  onRond,
}: {
  feuille: Feuille;
  onFiche: (id: string) => void;
  onRond: (bulle: Bulle) => void;
}) {
  const [par, setPar] = useState<Regroupement>(regroupementDuCouche);
  const regrouper = (r: Regroupement) => {
    choisirLeRegroupementDuCouche(r);
    setPar(r);
  };

  const ouvertes = useMemo(() => ouvertesDuCouche(feuille), [feuille]);
  const lus = useGroupes(feuille.bulle.id, par, ouvertes).data;
  const groupes = useMemo(() => (lus ? groupesALEcran(lus.groupes, lus.par, feuille) : []), [lus, feuille]);

  const analyse = feuille.analyse;
  const { faites, total } = analyse.avancement;
  const perles = perlesDeLAvancement(analyse);
  const barre = barreDeLAvancement(analyse.avancement);
  const echeances = enPeu(analyse.echeances);
  const temps = enPeu(ouPasseLeTemps(analyse.tempsParEtape));

  return (
    <div className="analyse-couchee">
      <div className="analyse-couchee-g">
        <section className="analyse-couchee-b">
          <h5>{AVANCEMENT}</h5>
          {perles ? (
            <span className="analyse-couchee-perles">
              {perles.map((p, i) => (
                <i key={i} className={p === 'reste' ? undefined : `est-${p}`} />
              ))}
            </span>
          ) : (
            <span className="analyse-couchee-barre">
              <i style={{ width: `${(barre.sorte === 'barre' ? barre.part : 0) * 100}%` }} />
            </span>
          )}
          <div className="analyse-couchee-chif">
            <span>
              <b>{faitesSurTotal(faites, total)}</b>
              {FAITES}
            </span>
            <span>
              <b>{dureeLisible(analyse.tempsQuiReste)}</b>
              {RESTANTES}
            </span>
            {analyse.sansDuree > 0 && (
              <span>
                <b>{analyse.sansDuree}</b>
                {SANS_DUREE}
              </span>
            )}
          </div>
        </section>

        <section className="analyse-couchee-b">
          <h5>{ECHEANCES_QUI_APPROCHENT}</h5>
          {echeances.montrees.length === 0 && <p className="analyse-couchee-note">{RIEN_DE_DATE_A_VENIR}</p>}
          {echeances.montrees.map((e) => (
            <div key={e.id} className={`analyse-couchee-ech${e.lueur === 'proche' ? ' est-proche' : ''}`}>
              <span className="analyse-couchee-j">{compteARebours(e.dans)}</span>
              <span>
                <b>{e.titre}</b>
                <br />
                {e.sorte === 'rendez_vous' ? RENDEZ_VOUS : ECHEANCE}, {jourAbrege(e.jour)}
              </span>
            </div>
          ))}
          {echeances.autres > 0 && <p className="analyse-couchee-note">{nAutres(echeances.autres)}</p>}
        </section>

        <section className="analyse-couchee-b">
          <h5>{OU_PASSE_LE_TEMPS}</h5>
          {temps.montrees.length === 0 && <p className="analyse-couchee-note">{AUCUNE_DUREE_REGLEE}</p>}
          {temps.montrees.map((l) => (
            <div key={l.id} className="analyse-couchee-cmp">
              <span className="analyse-couchee-n">{l.titre}</span>
              <span className="analyse-couchee-bar">
                <i style={{ width: `${l.longueur * 100}%` }} />
              </span>
              <span className="analyse-couchee-v">{dureeLisible(l.minutes)}</span>
            </div>
          ))}
          {temps.autres > 0 && <p className="analyse-couchee-note">{nAutres(temps.autres)}</p>}
        </section>
      </div>

      <div className="analyse-couchee-d">
        <div className="analyse-couchee-grp">
          {REGROUPER_PAR}
          <Curseur
            mini
            largeur={300}
            nom={REGROUPER_PAR}
            crans={CRANS_DU_REGROUPEMENT}
            choisi={par}
            onChoisir={regrouper}
            attribut="regrouper"
          />
        </div>
        <div className="analyse-couchee-cols">
          {groupes.map((g) => (
            <div key={g.cle} className="analyse-couchee-col" data-groupe={g.cle}>
              <div className="analyse-couchee-col-t">
                <b>{g.nom}</b>
                <span>{faitesSurTotal(g.compte.faites, g.compte.total)}</span>
              </div>
              {par === 'contact' && g.numero && (
                <div className="analyse-couchee-col-num">
                  <IconeCombine className="analyse-couchee-combine" />
                  {g.numero}
                </div>
              )}
              {cartesDuGroupe(g.bulles).map((b) => (
                <Carton key={b.id} bulle={b} branche={brancheDeLaCarte(b, feuille)} onFiche={onFiche} onRond={onRond} />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Une tâche en carte (`.carton`) : sa case, son titre, sa branche et sa date en petit. */
function Carton({
  bulle,
  branche,
  onFiche,
  onRond,
}: {
  bulle: BulleEnrichie;
  branche: string | null;
  onFiche: (id: string) => void;
  onRond: (bulle: Bulle) => void;
}) {
  const fait = bulle.etat === 'fait';
  const bout = ceQueDitLeBout(bulle);
  const petit = [branche, bout].filter(Boolean).join(' · ');
  return (
    <div
      className={`analyse-couchee-carton${fait ? ' est-fait' : ''}${attendLeMonde(bulle) ? ' est-en-attente' : ''}`}
      data-bulle={bulle.id}
    >
      <button
        type="button"
        className={`analyse-couchee-case${fait ? ' est-cochee' : ''}`}
        aria-label={titreDuRond(bulle.etat, true)}
        title={titreDuRond(bulle.etat, true)}
        onClick={() => onRond(bulle)}
      />
      <button type="button" className="analyse-couchee-ouvrir" onClick={() => onFiche(bulle.id)}>
        <span>{bulle.titre}</span>
        {petit && <small>{petit}</small>}
      </button>
    </div>
  );
}
